import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import Avatar from "../components/Avatar";

const CV_URL = `${process.env.PUBLIC_URL}/cv/james-carl-rhule-cv.docx`;
const THESIS_URL = `${process.env.PUBLIC_URL}/cv/msc-thesis-james-carl-rhule.docx`;

const FACTS = [
  { label: "Current Role", value: "Acting Regional Director, Centre for National Culture" },
  { label: "Education", value: "MSc Leadership & Organisational Development, UDS" },
  { label: "Focus", value: "Organisational Development, Research & Digital Transformation" },
  { label: "Location", value: "Bolgatanga, Upper East Region, Ghana" },
];

const STAT_STRIP = [
  { value: "20+", label: "Years in Public Service" },
  { value: "6", label: "Degrees & Diplomas" },
  { value: "87", label: "Thesis Respondents" },
  { value: "75%", label: "Variance Explained" },
];

const PRINCIPLES = [
  { title: "Lead", desc: "Strategic direction, accountable teams and measurable results." },
  { title: "Measure", desc: "Monitoring, evaluation and research that inform decisions." },
  { title: "Innovate", desc: "Digital tools and technical know-how that improve public service." },
];

const FOCUS_AREAS = [
  { title: "Organisational effectiveness", desc: "Building accountable, collaborative and performance-driven public institutions." },
  { title: "Digital transformation", desc: "Technology-enabled service delivery, databases and digital information management." },
  { title: "Education & ICT for young people", desc: "Equipping Junior High School students in Ghana with practical ICT skills, and shaping the policies that make that possible." },
  { title: "Culture & sustainable development", desc: "Preserving heritage and developing the economic value of culture and the creative arts." },
];

const EDUCATION_POINTS = [
  { label: "Since 2013", value: "Training students from Sekondi in ICT" },
  { label: "Focus", value: "ICT education for Junior High School students" },
  { label: "Policy", value: "Educational policy and access to digital skills" },
  { label: "Qualification", value: "Diploma in Education, University of Education, Winneba" },
];

const FEATURED_WORK = [
  {
    tag: "MSc Thesis · UDS, Tamale",
    foot: "2026 · Supervisor: Dr. Stephen Tetteh",
    title: "Structure, Leadership & Digital Technology in Public Institutions",
    desc: "A survey of 87 public-sector staff. Digital technology emerged as the strongest predictor of sustainable development, followed by leadership style and organisational structure.",
    to: "/research/public-institutions",
  },
  {
    tag: "Heritage · Upper East Region",
    foot: "2024 – 2025 · Coordinator",
    title: "Cultural Heritage Programmes",
    desc: "Coordinated the 2024 and 2025 Cultural Heritage Programmes, and Ghana's 2023 UNESCO Intangible Cultural Heritage inventory programme.",
  },
  {
    tag: "National Festival of Arts and Culture",
    foot: "2024 · Organiser",
    title: "NAFAC 2024, Techiman",
    desc: "Organised artists and artisans from the Upper East Region to take part in the National Festival of Arts and Culture in Bono East.",
  },
];

const SKILL_GROUPS = [
  { category: "Leadership & Organisational Development", items: ["Strategic planning", "Organisational change", "Team leadership", "Performance management"] },
  { category: "Public Sector Administration", items: ["Policy implementation", "Institutional coordination", "Report writing", "Budgeting & work plans"] },
  { category: "Research & Monitoring and Evaluation", items: ["Research design", "Data analysis", "Programme monitoring", "Impact assessment"] },
  { category: "Innovation & Digital Transformation", items: ["Database development", "Digital information management", "Process improvement"] },
  { category: "Education & ICT Training", items: ["ICT training for students", "Mathematics & Microsoft Office teaching", "Curriculum & classroom delivery", "Educational policy interest"] },
  { category: "Cultural Policy & Programmes", items: ["Heritage preservation", "Festival coordination", "Creative arts development"] },
  { category: "Technical & Engineering", items: ["Electrical & electronic systems", "Fault diagnosis", "CAD design", "Equipment maintenance"] },
];

const EXPERIENCE = [
  {
    date: "2025 – 2026",
    org: "Centre for National Culture, Bolgatanga",
    role: "Acting Regional Director",
    bullets: [
      "Lead regional operations: annual work plans, budgets, performance reports and staff appraisal.",
      "Coordinate cultural festivals, public education programmes, conferences and workshops, and oversee the Performance Management System.",
    ],
  },
  {
    date: "2023 – 2025",
    org: "Centre for National Culture, Bolgatanga",
    role: "Acting Deputy Regional Director",
    bullets: [
      "Coordinated regional programmes, research activities and administrative functions.",
      "Strengthened collaboration with public institutions, schools, cultural practitioners and development partners.",
    ],
  },
  {
    date: "2022 – 2023",
    org: "National Commission on Culture, Accra",
    role: "Head of Monitoring and Evaluation",
    bullets: [
      "Developed tools to assess programme implementation, outcomes and impact.",
      "Coordinated data collection and analysis that supported policy and management decisions.",
    ],
  },
  {
    date: "2017 – 2022",
    org: "Centre for National Culture, Fijai-Sekondi",
    role: "Head of Research and Documentation",
    bullets: [
      "Documented the region's artistic and cultural resources and promoted its cultural identity.",
      "Identified opportunities for cultural enterprise and supported research-based programme development.",
    ],
  },
  {
    date: "2006 – 2016",
    org: "Centre for National Culture, Fijai-Sekondi",
    role: "Technical Department",
    bullets: [
      "Maintained technical plant and equipment, and installed, inspected and repaired electrical and electronic equipment.",
      "Designed technical frameworks for cultural productions and supported the delivery of performances and events.",
    ],
  },
];

const CREDENTIALS = [
  { name: "MSc Leadership & Organisational Development", platform: "UDS, 2026" },
  { name: "BTech Tourism Management", platform: "TTU, 2019" },
  { name: "Diploma in Education", platform: "UEW, 2015" },
  { name: "Int'l Advanced Diploma in Computer Studies", platform: "NCC, 2011" },
  { name: "HND Electrical & Electronic Engineering", platform: "Takoradi Poly, 2004" },
  { name: "Senior Secondary Certificate (Science)", platform: "St. Augustine's, 1998" },
];

function SectionHeading({ num, eyebrow, title }) {
  return (
    <div className="mb-10 flex items-baseline gap-4">
      {num && <span className="font-serif text-sm text-ink-faint">{num}</span>}
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="mt-2 font-serif text-3xl font-medium text-teal-900 md:text-4xl">{title}</h2>
      </div>
    </div>
  );
}

export default function Home() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [hash]);

  return (
    <>
      <Nav />

      {/* HERO */}
      <section className="mx-auto max-w-content px-6 pb-16 pt-32 md:px-12 md:pb-24 md:pt-40">
        <div className="grid gap-14 md:grid-cols-[1.3fr_0.9fr] md:gap-10">
          <Reveal>
            <p className="eyebrow mb-5">Public Sector Leadership &middot; Upper East Region, Ghana</p>
            <h1 className="font-serif text-[3rem] font-medium leading-[1.05] text-teal-900 sm:text-[3.75rem] lg:text-[4.5rem]">
              James Carl-Rhule
            </h1>
            <p className="mt-6 max-w-[46ch] text-[1.05rem] leading-relaxed text-ink-soft">
              Acting Regional Director with experience in organisational development, research and
              documentation, monitoring and evaluation, cultural development and digital transformation.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
              <Link
                to="/initiatives"
                className="border border-teal-900 bg-teal-900 px-6 py-3 text-[0.72rem] uppercase tracking-widest2 text-white transition-colors hover:bg-teal-700"
              >
                View Experience
              </Link>
              <Link
                to={{ pathname: "/", hash: "#contact" }}
                className="text-[0.72rem] uppercase tracking-widest2 text-ink-soft transition-colors hover:text-teal-900"
              >
                Get in touch &rarr;
              </Link>
            </div>
          </Reveal>

          <Reveal delay={120} className="border border-line-strong p-7">
            <p className="eyebrow mb-5">At a Glance</p>
            <dl className="space-y-4">
              {FACTS.map((f) => (
                <div key={f.label} className="border-t border-line pt-3 first:border-t-0 first:pt-0">
                  <dt className="text-[0.65rem] uppercase tracking-widest2 text-ink-faint">{f.label}</dt>
                  <dd className="mt-1 font-serif text-[1.05rem] text-teal-900">{f.value}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      {/* THESIS SPOTLIGHT */}
      <section className="mx-auto max-w-content px-6 pb-16 md:px-12 md:pb-20">
        <Reveal className="grid gap-8 bg-teal-900 p-8 text-white md:grid-cols-[1.6fr_1fr] md:items-center md:gap-12 md:p-12">
          <div>
            <p className="text-[0.7rem] uppercase tracking-widest2 text-plum-300">Latest Research &middot; MSc Thesis</p>
            <h2 className="mt-3 font-serif text-3xl font-medium md:text-4xl">Leadership, Structure &amp; Digital Technology</h2>
            <p className="mt-4 max-w-[56ch] text-[1rem] leading-relaxed text-teal-100/85">
              A study of how organisational structure, leadership style and digital technology promote
              sustainable development in Ghana&rsquo;s public institutions. Together they explained 75%
              of the variation, with digital technology the strongest driver.
            </p>
          </div>
          <div className="md:text-right">
            <Link
              to="/research/public-institutions"
              className="inline-block border border-white px-7 py-3 text-[0.72rem] uppercase tracking-widest2 text-white transition-colors hover:bg-white hover:text-teal-900"
            >
              Read the study &rarr;
            </Link>
            <p className="mt-3 text-[0.72rem] text-teal-100/60">Degree award pending</p>
          </div>
        </Reveal>
      </section>

      {/* STAT STRIP */}
      <Reveal as="section" className="border-y border-line bg-stone-50">
        <div className="mx-auto grid max-w-content grid-cols-2 divide-x divide-line px-6 sm:grid-cols-4 md:px-12">
          {STAT_STRIP.map((s) => (
            <div key={s.label} className="px-2 py-8 text-center first:pl-0 sm:px-6">
              <div className="font-serif text-3xl text-teal-900">{s.value}</div>
              <div className="mt-1 text-[0.65rem] uppercase tracking-widest2 text-ink-faint">{s.label}</div>
            </div>
          ))}
        </div>
      </Reveal>

      {/* OBJECTIVE */}
      <section id="story" className="mx-auto max-w-content px-6 py-20 md:px-12 md:py-28">
        <Reveal className="max-w-[62ch]">
          <p className="eyebrow">Career Objective</p>
          <h2 className="mt-2 font-serif text-3xl font-medium text-teal-900 md:text-4xl">Institutions That Perform</h2>
          <div className="mt-6 space-y-5 text-[0.98rem] leading-[1.9] text-ink-soft">
            <p>
              I aim to provide strategic leadership that improves organisational effectiveness,
              innovation and sustainable development, through evidence-based management, digital
              transformation and human resource development.
            </p>
            <p>
              I want to help institutions build accountable, collaborative, performance-driven cultures
              that promote continuous improvement, efficient use of resources and measurable development
              outcomes.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-px border border-line bg-line sm:grid-cols-3">
          {PRINCIPLES.map((c, i) => (
            <Reveal key={c.title} delay={i * 80} className="bg-white p-6">
              <h3 className="font-serif text-xl text-teal-900">{c.title}</h3>
              <p className="mt-2 text-[0.9rem] leading-relaxed text-ink-soft">{c.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FOCUS */}
      <section id="focus" className="border-y border-line bg-stone-50">
        <div className="mx-auto max-w-content px-6 py-20 md:px-12 md:py-28">
          <SectionHeading eyebrow="Areas of Focus" title="Where I Aim to Add Value" />
          <div className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
            {FOCUS_AREAS.map((g, i) => (
              <Reveal key={g.title} delay={i * 80} className="bg-white p-6">
                <h3 className="font-serif text-lg text-teal-900">{g.title}</h3>
                <p className="mt-2 text-[0.9rem] leading-relaxed text-ink-soft">{g.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EDUCATION & ICT */}
      <section id="education" className="mx-auto max-w-content px-6 py-20 md:px-12 md:py-28">
        <div className="grid gap-12 md:grid-cols-[1.1fr_1fr] md:gap-16">
          <Reveal>
            <p className="eyebrow">Education &amp; Educational Policy</p>
            <h2 className="mt-2 font-serif text-3xl font-medium text-teal-900 md:text-4xl">
              ICT Education for Young Ghanaians
            </h2>
            <div className="mt-6 space-y-5 text-[0.98rem] leading-[1.9] text-ink-soft">
              <p>
                Alongside public administration and research, I am deeply interested in education and
                educational policy, especially ICT education for Junior High School students in Ghana.
                Digital skills learned early open doors to further study, work and enterprise.
              </p>
              <p>
                Since 2013 I have been training students from Sekondi in ICT. That hands-on teaching
                sits on top of my Diploma in Education and my earlier work as a class teacher and a
                teacher of Mathematics and Microsoft Office.
              </p>
            </div>
            <Link
              to="/initiatives"
              className="mt-6 inline-block text-[0.72rem] uppercase tracking-widest2 text-teal-700 hover:text-teal-900"
            >
              See the teaching &amp; training work &rarr;
            </Link>
          </Reveal>

          <Reveal delay={120} className="border border-line-strong p-7">
            <p className="eyebrow mb-5">At the Heart of It</p>
            <dl className="space-y-4">
              {EDUCATION_POINTS.map((p) => (
                <div key={p.label} className="border-t border-line pt-3 first:border-t-0 first:pt-0">
                  <dt className="text-[0.65rem] uppercase tracking-widest2 text-ink-faint">{p.label}</dt>
                  <dd className="mt-1 font-serif text-[1.05rem] text-teal-900">{p.value}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      {/* FEATURED WORK */}
      <section className="mx-auto max-w-content px-6 py-20 md:px-12 md:py-28">
        <SectionHeading num="01" eyebrow="Selected Work" title="Programmes & Research" />
        <div className="divide-y divide-line border-t border-line">
          {FEATURED_WORK.map((p, i) => (
            <Reveal key={p.title} delay={i * 70} className="grid gap-2 py-7 md:grid-cols-[1fr_2fr] md:gap-10">
              <div>
                <p className="text-[0.68rem] uppercase tracking-widest2 text-teal-500">{p.tag}</p>
                <p className="mt-2 text-[0.72rem] uppercase tracking-widest2 text-ink-faint">{p.foot}</p>
              </div>
              <div>
                <h3 className="font-serif text-xl text-teal-900">{p.title}</h3>
                <p className="mt-2 max-w-[60ch] text-[0.92rem] leading-relaxed text-ink-soft">{p.desc}</p>
                {p.to && (
                  <Link to={p.to} className="mt-3 inline-block text-[0.72rem] uppercase tracking-widest2 text-teal-700 hover:text-teal-900">
                    Read more &rarr;
                  </Link>
                )}
              </div>
            </Reveal>
          ))}
        </div>
        <Link to="/initiatives" className="mt-8 inline-block text-[0.72rem] uppercase tracking-widest2 text-teal-700 hover:text-teal-900">
          See all experience &amp; programmes &rarr;
        </Link>
      </section>

      {/* SKILLS */}
      <section id="skills" className="border-y border-line bg-stone-50">
        <div className="mx-auto max-w-content px-6 py-20 md:px-12 md:py-28">
          <SectionHeading num="02" eyebrow="Competencies" title="Core Skills" />
          <div className="grid gap-x-10 gap-y-6 md:grid-cols-2">
            {SKILL_GROUPS.map((g, i) => (
              <Reveal key={g.category} delay={i * 50} className="border-t border-line pt-4">
                <h3 className="font-serif text-base text-teal-900">{g.category}</h3>
                <p className="mt-1 text-[0.88rem] leading-relaxed text-ink-soft">{g.items.join(" · ")}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section id="experience" className="mx-auto max-w-content px-6 py-20 md:px-12 md:py-28">
        <SectionHeading num="03" eyebrow="Career" title="Professional Experience" />
        <div className="border-t border-line">
          {EXPERIENCE.map((e) => (
            <Reveal as="div" key={e.role + e.date} className="grid gap-3 border-b border-line py-7 md:grid-cols-[200px_1fr] md:gap-10">
              <div>
                <div className="text-[0.7rem] uppercase tracking-widest2 text-teal-500">{e.date}</div>
                <div className="mt-1 text-[0.85rem] text-ink-soft">{e.org}</div>
              </div>
              <div>
                <h3 className="font-serif text-lg text-teal-900">{e.role}</h3>
                <ul className="mt-2 space-y-1.5">
                  {e.bullets.map((b) => (
                    <li key={b} className="flex gap-3 text-[0.9rem] leading-relaxed text-ink-soft">
                      <span className="mt-[0.55em] h-1 w-1 flex-shrink-0 rounded-full bg-plum-400" aria-hidden="true" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="border-y border-line bg-stone-50">
        <div className="mx-auto max-w-content px-6 py-20 md:px-12 md:py-28">
          <div className="grid gap-14 md:grid-cols-[1.3fr_1fr] md:gap-16">
            <Reveal>
              <div className="flex flex-col gap-7 sm:flex-row sm:items-center">
                <Avatar
                  src={`${process.env.PUBLIC_URL}/images/james.webp`}
                  alt="James Carl-Rhule"
                  initials="JC"
                  className="w-52 shadow-sm sm:w-64"
                />
                <div>
                  <p className="eyebrow">About Me</p>
                  <h2 className="mt-2 font-serif text-3xl font-medium text-teal-900 md:text-4xl">Public Servant &amp; Researcher</h2>
                </div>
              </div>
              <p className="mt-7 max-w-[52ch] text-[0.98rem] leading-[1.9] text-ink-soft">
                I&rsquo;m a public-sector professional with extensive experience in leadership,
                administration, research, monitoring and evaluation, cultural development and technical
                operations. I have completed the MSc in Leadership and Organisational Development at the
                University for Development Studies, Tamale, and am awaiting the formal award.
              </p>
              <p className="mt-4 max-w-[52ch] text-[0.98rem] leading-[1.9] text-ink-soft">
                I combine academic knowledge with practical work in public administration, research,
                innovation and technology, and I am interested in using innovation to improve public
                service delivery.
              </p>
              <p className="mt-4 max-w-[52ch] text-[0.98rem] leading-[1.9] text-ink-soft">
                Education is a long-standing interest. I hold a Diploma in Education, have taught
                Mathematics and Microsoft Office, and since 2013 I have trained students from Sekondi
                in ICT. I care about how ICT education and educational policy can serve Junior High
                School students across Ghana.
              </p>
              <dl className="mt-8 border-t border-line pt-5">
                <dt className="text-[0.65rem] uppercase tracking-widest2 text-teal-500">Education</dt>
                <dd className="mt-2 font-serif text-lg text-teal-900">MSc Leadership and Organisational Development</dd>
                <dd className="mt-1 text-[0.85rem] text-ink-soft">University for Development Studies, Tamale &middot; 2025 &ndash; 2026</dd>
              </dl>
            </Reveal>

            <Reveal delay={120} className="space-y-7 md:pt-24">
              <div className="border-t border-line pt-5">
                <p className="text-[0.65rem] uppercase tracking-widest2 text-teal-500">Leadership</p>
                <p className="mt-2 text-[0.9rem] leading-relaxed text-ink-soft">
                  Assistant Divisional Secretary, PSWU &middot; Local Union Chairman &middot; Sekondi
                  Branch Secretary &middot; Estate Officer, Centre for National Culture
                </p>
              </div>
              <div className="border-t border-line pt-5">
                <p className="text-[0.65rem] uppercase tracking-widest2 text-teal-500">Beyond Work</p>
                <p className="mt-2 text-[0.9rem] leading-relaxed text-ink-soft">
                  ICT training for students in Sekondi (since 2013) &middot; Part-time teaching in Mathematics and Microsoft Office &middot; Volunteer in polio
                  immunisation and national elections &middot; Reading, travel, documentaries and soccer
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section className="mx-auto max-w-content px-6 py-20 md:px-12 md:py-28">
        <SectionHeading num="04" eyebrow="Credentials" title="Qualifications" />
        <div className="grid divide-y divide-line border-y border-line sm:grid-cols-2 sm:divide-x sm:divide-y-0">
          {CREDENTIALS.map((c, i) => (
            <Reveal key={c.name} delay={i * 30} className="flex items-baseline justify-between gap-4 py-4 sm:px-6 sm:first:pl-0">
              <span className="text-[0.9rem] text-ink-soft">{c.name}</span>
              <span className="whitespace-nowrap text-[0.65rem] uppercase tracking-widest2 text-ink-faint">{c.platform}</span>
            </Reveal>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
          <a href={CV_URL} className="text-[0.72rem] uppercase tracking-widest2 text-teal-700 hover:text-teal-900">
            Download CV &rarr;
          </a>
          <a href={THESIS_URL} className="text-[0.72rem] uppercase tracking-widest2 text-teal-700 hover:text-teal-900">
            Download MSc thesis &rarr;
          </a>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-teal-900">
        <div className="mx-auto max-w-content px-6 py-20 text-center md:px-12 md:py-28">
          <p className="text-[0.7rem] uppercase tracking-widest2 text-teal-300">Let&rsquo;s Connect</p>
          <h2 className="mt-3 font-serif text-3xl font-medium text-white md:text-4xl">Get in Touch</h2>
          <p className="mx-auto mt-5 max-w-[48ch] text-[0.95rem] leading-relaxed text-teal-100/80">
            Open to leadership, research and partnership opportunities in public administration,
            organisational development and cultural development.
          </p>

          <div className="mx-auto mt-12 grid max-w-3xl grid-cols-1 divide-y divide-white/15 border-y border-white/15 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            <div className="px-0 py-5 sm:px-6 sm:first:pl-0">
              <div className="text-[0.65rem] uppercase tracking-widest2 text-teal-300">Email</div>
              <a href="mailto:jcarlrhule.ncc@gmail.com" className="mt-1 block text-[0.9rem] text-white hover:text-teal-100">
                jcarlrhule.ncc@gmail.com
              </a>
              <a href="mailto:carl.rhule@nccg.gov.gh" className="mt-1 block text-[0.9rem] text-white hover:text-teal-100">
                carl.rhule@nccg.gov.gh
              </a>
            </div>
            <div className="px-0 py-5 sm:px-6">
              <div className="text-[0.65rem] uppercase tracking-widest2 text-teal-300">Phone</div>
              <a href="tel:+233543258912" className="mt-1 block text-[0.9rem] text-white hover:text-teal-100">
                0543 258 912
              </a>
              <a href="tel:+233201854680" className="mt-1 block text-[0.9rem] text-white hover:text-teal-100">
                0201 854 680
              </a>
            </div>
            <div className="px-0 py-5 sm:px-6 sm:last:pr-0">
              <div className="text-[0.65rem] uppercase tracking-widest2 text-teal-300">Address</div>
              <div className="mt-1 text-[0.9rem] text-white">
                Centre for National Culture, P. O. Box 116, Bolgatanga, Upper East Region
              </div>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <a
              href="mailto:jcarlrhule.ncc@gmail.com"
              className="inline-block border border-white px-7 py-3 text-[0.72rem] uppercase tracking-widest2 text-white transition-colors hover:bg-white hover:text-teal-900"
            >
              Send a Message
            </a>
            <a
              href="https://www.linkedin.com/in/jamescarlrhule/"
              target="_blank"
              rel="noreferrer"
              className="inline-block border border-white/40 px-7 py-3 text-[0.72rem] uppercase tracking-widest2 text-white transition-colors hover:border-white"
            >
              LinkedIn
            </a>
            <a
              href={CV_URL}
              className="inline-block border border-white/40 px-7 py-3 text-[0.72rem] uppercase tracking-widest2 text-white transition-colors hover:border-white"
            >
              Download CV
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
